<template>
  <Base>
    <template #toolbar>
      <div class="toolbar" id="kt_toolbar">
        <div class="container-fluid d-flex flex-stack" id="kt_toolbar_container">
          <div class="d-flex align-items-center me-3">
            <span class="h-20px border-gray-200 border-start mx-4"></span>
            <ul class="breadcrumb breadcrumb-separatorless fw-bold fs-7 my-1">
              <li class="breadcrumb-item text-muted">
                <router-link :to="{ name: 'dashboard' }" class="text-muted text-hover-primary">
                  <i class="fad fa-home"></i> Tableau de bord
                </router-link>
              </li>
              <li class="breadcrumb-item"><span class="bullet bg-gray-200 w-5px h-2px"></span></li>
              <li class="breadcrumb-item text-dark">Directives IA</li>
            </ul>
          </div>
          <div class="d-flex align-items-center py-1 gap-2">
            <button class="btn btn-light-primary btn-sm" @click="seedDefault" :disabled="seeding">
              <span v-if="seeding" class="spinner-border spinner-border-sm me-2"></span>
              <i v-else class="fad fa-magic-wand-sparkles me-1"></i>
              Initialiser directive par défaut
            </button>
            <button class="btn btn-primary btn-sm" @click="resetForm" v-b-modal="'directiveModal'">
              <i class="fad fa-plus me-1"></i> Nouvelle directive
            </button>
          </div>
        </div>
      </div>
    </template>
    <template #content>
      <div class="card">
        <div class="card-body">
          <div v-if="directives.length === 0" class="text-center text-muted py-10">
            <i class="fad fa-brain fs-3x mb-3 d-block"></i>
            <div class="mb-3">Aucune directive IA. Clique sur <b>"Initialiser directive par défaut"</b> en haut pour créer une directive complète avec détection d'intent, mapping des formulaires, et ton convivial.</div>
            <button class="btn btn-primary" @click="seedDefault" :disabled="seeding">
              <span v-if="seeding" class="spinner-border spinner-border-sm me-2"></span>
              <i v-else class="fad fa-magic-wand-sparkles me-1"></i>
              Initialiser maintenant
            </button>
          </div>

          <div v-for="d in directives" :key="d.id" class="border rounded p-5 mb-5" :class="{'border-success': d.isActive, 'border-dashed': !d.isActive}">
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <div class="d-flex align-items-center gap-3 mb-2">
                  <span class="badge fs-7" :class="contextBadge(d.context)">{{ d.context }}</span>
                  <span v-if="d.isActive" class="badge badge-light-success">Actif</span>
                  <span v-else class="badge badge-light-danger">Inactif</span>
                  <span class="text-muted fs-8">Priorite : {{ d.ranking }}</span>
                  <span class="text-muted fs-8">Max tours : {{ d.maxTurns }}</span>
                </div>
                <div class="text-muted fs-7 mb-2" style="white-space: pre-line; max-height: 100px; overflow: hidden;">{{ d.systemPrompt }}</div>
                <div class="d-flex gap-2 flex-wrap">
                  <span v-for="a in (d.allowedActions || [])" :key="a" class="badge badge-light-primary fs-8">{{ a }}</span>
                </div>
                <div v-if="d.allowedForms && d.allowedForms.length > 0" class="mt-1">
                  <span v-for="f in d.allowedForms" :key="f" class="badge badge-light-info fs-8 me-1">{{ f }}</span>
                </div>
              </div>
              <div class="d-flex gap-2">
                <button class="btn btn-sm btn-light-warning" @click="editDirective(d)" v-b-modal="'directiveModal'">
                  <i class="fad fa-edit"></i>
                </button>
                <button class="btn btn-sm btn-light-danger" @click="deleteDirective(d)">
                  <i class="fad fa-trash"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal Create/Edit -->
      <b-modal id="directiveModal" :title="form.id ? 'Modifier la directive' : 'Nouvelle directive'" centered size="xl" @ok.prevent="saveDirective">
        <div class="row">
          <div class="col-4 mb-3">
            <label class="required form-label">Contexte</label>
            <b-form-select v-model="form.context" :options="contextOptions"></b-form-select>
          </div>
          <div class="col-4 mb-3">
            <label class="form-label">Priorite (ranking)</label>
            <b-form-input v-model.number="form.ranking" type="number"></b-form-input>
            <div class="text-muted fs-8">Plus le chiffre est eleve, plus la directive est prioritaire</div>
          </div>
          <div class="col-4 mb-3">
            <label class="form-label">Max tours avant escalade</label>
            <b-form-input v-model.number="form.maxTurns" type="number"></b-form-input>
          </div>

          <div class="col-12 mb-3">
            <label class="required form-label">Instructions (System Prompt)</label>
            <b-form-textarea v-model="form.systemPrompt" rows="10" placeholder="Decris le comportement attendu de l'IA. Ex: Tu es l'assistant WiASSUR. Tu aides les clients a souscrire une assurance..."></b-form-textarea>
            <div class="text-muted fs-8 mt-1">Ecris en langage naturel. L'IA suivra ces instructions pour chaque message du client.</div>
          </div>

          <div class="col-12 mb-3">
            <label class="form-label">Actions autorisees</label>
            <div class="d-flex flex-wrap gap-3">
              <b-form-checkbox v-for="action in allActions" :key="action.code"
                               :value="action.code"
                               v-model="form.allowedActions">
                <div>
                  <span class="fw-bolder">{{ action.code }}</span>
                  <div class="text-muted fs-8">{{ action.label }}</div>
                </div>
              </b-form-checkbox>
            </div>
          </div>

          <div class="col-12 mb-3">
            <label class="form-label">Formulaires autorises</label>
            <div class="d-flex flex-wrap gap-2">
              <b-form-checkbox v-for="f in availableForms" :key="f.slug"
                               :value="f.slug"
                               v-model="form.allowedForms">
                {{ f.label || f.slug }}
              </b-form-checkbox>
            </div>
            <div v-if="availableForms.length === 0" class="text-muted fs-8">Chargement des formulaires...</div>
          </div>

          <div class="col-12 mb-3">
            <b-form-checkbox v-model="form.isActive">Directive active</b-form-checkbox>
          </div>
        </div>
      </b-modal>
    </template>
  </Base>
</template>

<script lang="ts">
import {Component, Vue} from 'vue-property-decorator';
import Base from "@/layouts/Base.vue";
import {api} from "@/services/Api";
import Swal from "sweetalert2";

@Component({components: {Base}})
export default class AiDirectiveManager extends Vue {
  directives: any[] = []
  form: any = {}
  availableForms: any[] = []
  seeding = false

  contextOptions = [
    {value: 'GLOBAL', text: 'GLOBAL — Tous les cas'},
    {value: 'RENEWAL', text: 'RENEWAL — Renouvellement'},
    {value: 'NEW_BUSINESS', text: 'NEW_BUSINESS — Nouvelle affaire'},
    {value: 'CLAIM', text: 'CLAIM — Sinistre'},
    {value: 'PARTNER', text: 'PARTNER — Partenaire'},
    {value: 'SUPPORT', text: 'SUPPORT — Support general'},
    {value: 'chat_humanizer', text: 'chat_humanizer — Reformulation (nouveau moteur)'},
    {value: 'chat_nlu', text: 'chat_nlu — Compréhension/NLU (nouveau moteur)'},
    {value: 'chat_faq', text: 'chat_faq — Réponses FAQ (nouveau moteur)'},
  ]

  allActions = [
    {code: 'FILL_FORM', label: 'Demarrer un formulaire de cotation'},
    {code: 'ANSWER_FORM_QUESTION', label: 'Repondre a une question du formulaire'},
    {code: 'REDIRECT_FORM', label: 'Changer de formulaire'},
    {code: 'SEND_RENEWAL_LINK', label: 'Envoyer un lien de renouvellement'},
    {code: 'SEND_PAYMENT_LINK', label: 'Envoyer un lien de paiement'},
    {code: 'INFO', label: 'Repondre a une question informative'},
    {code: 'ESCALATE', label: 'Escalader vers un agent humain'},
    {code: 'CALL', label: 'Declencher un appel vocal IA'},
  ]

  contextBadge(ctx: string) {
    const map: any = {
      GLOBAL: 'badge-primary',
      RENEWAL: 'badge-warning',
      NEW_BUSINESS: 'badge-success',
      CLAIM: 'badge-danger',
      PARTNER: 'badge-info',
      SUPPORT: 'badge-secondary',
      chat_humanizer: 'badge-light-primary',
      chat_nlu: 'badge-light-info',
      chat_faq: 'badge-light-success',
    }
    return map[ctx] || 'badge-secondary'
  }

  mounted() {
    this.loadDirectives()
    this.loadForms()
  }

  resetForm() {
    this.form = {
      context: 'GLOBAL',
      systemPrompt: '',
      allowedActions: ['FILL_FORM', 'ANSWER_FORM_QUESTION', 'INFO', 'ESCALATE'],
      allowedForms: [],
      maxTurns: 15,
      ranking: 0,
      isActive: true,
    }
  }

  editDirective(d: any) {
    this.form = {
      ...d,
      allowedActions: d.allowedActions || [],
      allowedForms: d.allowedForms || [],
    }
  }

  async loadDirectives() {
    this.$store.commit('loading')
    const res = await api.get(api.chat, 'ai/directive/list')
    this.$store.commit('stopLoading')
    if (res?.data?.directives) {
      this.directives = res.data.directives
    }
  }

  async loadForms() {
    try {
      const res = await api.get(api.form, 'survey/list')
      if (res?.data?.surveys) {
        this.availableForms = res.data.surveys.map((s: any) => ({slug: s.slug, label: s.label}))
      }
    } catch (e) {
      // pas critique
    }
  }

  async saveDirective() {
    this.$store.commit('loading')
    const res = await api.post(api.chat, 'ai/directive/edit', this.form)
    this.$store.commit('stopLoading')
    if (res && res.data) {
      this.$bvModal?.hide?.('directiveModal')
      await this.loadDirectives()
    }
  }

  async seedDefault() {
    const hasExisting = this.directives.some((d: any) => d.context === 'GLOBAL')
    if (hasExisting) {
      const confirm = await Swal.fire({
        title: 'Remplacer la directive existante ?',
        html: 'Une directive <code>GLOBAL</code> existe deja. Continuer remplacera son systemPrompt et ses actions par la version par defaut.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Remplacer',
        cancelButtonText: 'Annuler',
      })
      if (!confirm.isConfirmed) return
    }
    this.seeding = true
    try {
      const res = await api.post(api.chat, 'ai/directive/seed-default', {force: true})
      await Swal.fire({
        icon: 'success',
        title: res?.data?.message || 'Directive initialisee',
        timer: 1500,
        showConfirmButton: false,
      })
      await this.loadDirectives()
    } catch (e: any) {
      await Swal.fire({icon: 'error', title: 'Echec', text: e?.message || 'Erreur'})
    } finally {
      this.seeding = false
    }
  }

  async deleteDirective(d: any) {
    if (!confirm('Supprimer cette directive ?')) return
    await api.get(api.chat, 'ai/directive/delete/' + d.id)
    await this.loadDirectives()
  }
}
</script>
